/* eslint-disable react-hooks/exhaustive-deps */
import { Button, Form, FormProps, Input, Spin } from 'antd';
import { useContext, useEffect, useState } from 'react';
import { AuthContext } from 'contexts/AuthContext';
import styles from './WelcomeHome.module.css';
import { useFloatNotification } from 'contexts/FloatNotificationContext';
import { useTranslation } from 'react-i18next';
import { useWeb3 } from 'contexts/Web3Context';
import { useValidateUserEmail } from 'api/services/userService';
import { DarwinAlert } from 'components/darwin-ui/Alert';
import { useWeb3Auth } from '@web3auth/no-modal-react-hooks';
import { isDummyLogin } from 'services/api/utils';

export const WelcomeHome = () => {
  const { t } = useTranslation();
  const { address, connect, signMessage, isInitialized, isInitializing } =
    useWeb3();

  const { openNotification } = useFloatNotification();
  const { login, isAuthenticated, loginWithEmail } = useContext(AuthContext);
  const [form] = Form.useForm();
  const [email, setEmail] = useState<string>('');
  const { web3Auth } = useWeb3Auth();
  const dummyLogin = isDummyLogin();
  const isLogin = !dummyLogin && web3Auth?.status === 'connecting';
  const [validationTrigger, setValidationTrigger] = useState(0);
  const { data: isGettingValidation, refetch: isRefetchingDarwinValidation } =
    useValidateUserEmail({ email: email || '' });
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchToken = async () => {
      if (address && !isAuthenticated) {
        try {
          setIsLoading(true);
          const nonce =
            'login: ' + Math.random() * (10000000000 - 10000) + 10000;
          const sig = await signMessage(nonce);
          login(nonce, sig);
        } catch (e: any) {
          openNotification('Login', e.message, 'error');
        } finally {
          setIsLoading(false);
        }
      }
    };
    fetchToken();
  }, [address, isAuthenticated, login, openNotification, signMessage]);

  useEffect(() => {
    if (address && email) {
      localStorage.setItem('darwin_user_email', email);
    }
  }, [address, email]);

  // Process login.
  const handleFinish = async (values: any) => {
    const { email: newEmail } = values;
    setEmail(newEmail);
    setValidationTrigger(prev => prev + 1);
  };

  useEffect(() => {
    if (!email) return;
    const validateAndConnect = async () => {
      try {
        const validationResponse = await isRefetchingDarwinValidation();
        if (validationResponse?.data?.data) {
          if (process.env.REACT_APP_USE_DUMMY_LOGIN === 'true') {
            try {
              await loginWithEmail(email);
            } catch (err: any) {
              openNotification('Login failed', err.message, 'error');
            }
          } else {
            // REAL FLOW: popup Web3Auth
            await connect(email);
          }
        }
      } catch (error: any) {
        openNotification('Validation Error', error.message, 'error');
      }
    };
    validateAndConnect();
  }, [validationTrigger]);

  const onFinishFailed: FormProps['onFinishFailed'] = errorInfo => {
    openNotification('Error', errorInfo.errorFields[0].errors[0], 'error');
  };

  return (
    <div className={styles.container}>
      <title>This needs to be styled</title>
      <Spin spinning={isLoading || isInitializing} fullscreen />

      <header className={styles.header}>
        <img src="darwin_logo.png" alt="darwin-logo" className={styles.logo} />
        {/* this will be implemented in the future 
        <div className={styles.registerContainer}>
          <span className={styles.newCustomer}>{t('new-customer?')}</span>
          <Button type="primary" onClick={handleEnroll}>
            {t('enroll-now')}
          </Button>
        </div> 
        */}
      </header>

      <main className={styles.main} />

      {!isLoading && isInitialized && (
        <div className={styles.modal}>
          <div className={styles.modalHeader}>{t('welcome-title')}</div>
          <div className={styles.modalBody}>
            <Form
              form={form}
              onFinish={handleFinish}
              onFinishFailed={onFinishFailed}
              layout="vertical"
              className={styles.userForm}
            >
              <Form.Item
                name="email"
                rules={[
                  { required: true, message: t('email-required') },
                  { type: 'email', message: t('invalid-email') },
                ]}
              >
                <Input />
              </Form.Item>

              {isGettingValidation && !isGettingValidation?.data && (
                <DarwinAlert
                  className={styles.invalidEmailAlert}
                  description={t('onboarding.invalid-email')}
                  type="error"
                  showIcon
                />
              )}

              <Form.Item>
                <Button
                  className={styles.socialLoginButton}
                  htmlType="submit"
                  disabled={isLogin}
                >
                  {t('login')}
                </Button>
              </Form.Item>
            </Form>
          </div>
        </div>
      )}
    </div>
  );
};
